import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import axios from "axios";

import SmoothScroll from "@/components/SmoothScroll";
import { Grain } from "@/components/Motion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Intro from "@/components/Intro";
import CartDrawer from "@/components/CartDrawer";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";

import Home from "@/pages/Home";
import DimensionI from "@/pages/DimensionI";
import DimensionPage from "@/pages/DimensionPage";
import Music from "@/pages/Music";
import ReleaseDetail from "@/pages/ReleaseDetail";
import TrackPage from "@/pages/TrackPage";
import Visuals from "@/pages/Visuals";
import Artist from "@/pages/Artist";
import Join from "@/pages/Join";
import Store from "@/pages/Store";
import StoreProduct from "@/pages/StoreProduct";
import CompleteSet from "@/pages/CompleteSet";
import DimensionV from "@/pages/DimensionV";
import DimensionVAccess from "@/pages/DimensionVAccess";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Auth from "@/pages/Auth";
import Account from "@/pages/Account";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import ConfirmEmailChange from "@/pages/ConfirmEmailChange";
import InnerCircle from "@/pages/InnerCircle";
import SupportersPage from "@/pages/SupportersPage";
import AdminWhispers from "@/pages/AdminWhispers";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminReleases from "@/pages/admin/AdminReleases";
import AdminReleaseEdit from "@/pages/admin/AdminReleaseEdit";
import AdminDimensions from "@/pages/admin/AdminDimensions";
import AdminVisuals from "@/pages/admin/AdminVisuals";
import AdminStore from "@/pages/admin/AdminStore";
import AdminVault from "@/pages/admin/AdminVault";
import AdminInnerCircle from "@/pages/admin/AdminInnerCircle";
import AdminNetwork from "@/pages/admin/AdminNetwork";
import AdminSupporters from "@/pages/admin/AdminSupporters";
import AdminDimensionV from "@/pages/admin/AdminDimensionV";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import { PlayerProvider } from "@/context/PlayerContext";
import { SiteSettingsProvider } from "@/context/SiteSettingsContext";
import MiniPlayer from "@/components/MiniPlayer";
import { trackPageView } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScrollManager() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 400);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}

function ReferralCapture() {
  const location = useLocation();
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (ref) {
      localStorage.setItem("mm_ref", JSON.stringify({ code: ref.toUpperCase(), exp: Date.now() + 30 * 86400000 }));
      axios.post(`${API}/referral/click`, { code: ref, path: location.pathname }).catch(() => {});
    }
  }, [location]);
  return null;
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    if (!location.pathname.startsWith("/admin")) trackPageView(location.pathname);
  }, [location]);
  return null;
}

const PUBLIC_ROUTES = (
  <>
    <Route path="/" element={<Home />} />
    <Route path="/dimension-i" element={<DimensionI />} />
    <Route path="/dimension-ii" element={<DimensionPage number="02" />} />
    <Route path="/dimension-iii" element={<DimensionPage number="03" />} />
    <Route path="/dimension-iv" element={<DimensionPage number="04" />} />
    <Route path="/dimension-v" element={<DimensionV />} />
    <Route path="/dimension-v/access" element={<DimensionVAccess />} />
    <Route path="/music" element={<Music />} />
    <Route path="/music/:slug" element={<ReleaseDetail />} />
    <Route path="/track/:slug" element={<TrackPage />} />
    <Route path="/complete-set" element={<CompleteSet />} />
    <Route path="/visuals" element={<Visuals />} />
    <Route path="/artist" element={<Artist />} />
    <Route path="/join" element={<Join />} />
    <Route path="/inner-circle" element={<InnerCircle />} />
    <Route path="/supporters" element={<SupportersPage />} />
    <Route path="/store" element={<Store />} />
    <Route path="/store/:lookupKey" element={<StoreProduct />} />
    <Route path="/payment/success" element={<PaymentSuccess />} />
    <Route path="/payment/cancel" element={<PaymentCancel />} />
    <Route path="/login" element={<Auth mode="login" />} />
    <Route path="/register" element={<Auth mode="register" />} />
    <Route path="/account" element={<Account />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/verify-email" element={<VerifyEmail />} />
    <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
  </>
);

const ADMIN_ROUTES = (
  <Route path="/admin" element={<AdminLayout />}>
    <Route index element={<AdminDashboard />} />
    <Route path="homepage" element={<AdminHomepage />} />
    <Route path="media" element={<AdminMedia />} />
    <Route path="releases" element={<AdminReleases />} />
    <Route path="releases/:id" element={<AdminReleaseEdit />} />
    <Route path="dimensions" element={<AdminDimensions />} />
    <Route path="visuals" element={<AdminVisuals />} />
    <Route path="store" element={<AdminStore />} />
    <Route path="vault" element={<AdminVault />} />
    <Route path="inner-circle" element={<AdminInnerCircle />} />
    <Route path="network" element={<AdminNetwork />} />
    <Route path="supporters" element={<AdminSupporters />} />
    <Route path="whispers" element={<AdminWhispers />} />
    <Route path="dimension-v" element={<AdminDimensionV />} />
    <Route path="analytics" element={<AdminAnalytics />} />
    <Route path="settings" element={<AdminSettings />} />
  </Route>
);

function Shell({ showIntro, setShowIntro }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <Routes>
        {ADMIN_ROUTES}
        {PUBLIC_ROUTES}
      </Routes>
    );
  }

  return (
    <SmoothScroll>
      <AnimatePresence>
        {showIntro && <Intro onComplete={() => setShowIntro(false)} />}
      </AnimatePresence>
      <ScrollManager />
      <Nav />
      <CartDrawer />
      <main>
        <Routes>
          {PUBLIC_ROUTES}
          {ADMIN_ROUTES}
        </Routes>
      </main>
      <Footer />
      <MiniPlayer />
    </SmoothScroll>
  );
}

function App() {
  const [showIntro, setShowIntro] = useState(
    () => typeof window !== "undefined" && window.location.pathname === "/"
  );

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) setShowIntro(false);
  }, []);

  return (
    <div className="App">
      <Grain />
      <Toaster theme="dark" position="bottom-center" />
      <BrowserRouter>
        <AuthProvider>
          <SiteSettingsProvider>
          <CartProvider>
            <PlayerProvider>
              <ReferralCapture />
              <PageViewTracker />
              <Shell showIntro={showIntro} setShowIntro={setShowIntro} />
            </PlayerProvider>
          </CartProvider>
          </SiteSettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
